import React, { createContext, useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import api from '../services/api';
import { getAppCurrency, setAppCurrency as persistAppCurrency, APP_CURRENCY_CHANGE_EVENT } from '../utils/currency';
import animalHierarchyCache from '../services/animalHierarchyCache';
import { isPublicAppPath } from '../utils/publicAppPaths';

const AppDataContext = createContext(null);

export const AppDataProvider = ({ children }) => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);

  // Data state
  const [farmsWithStructure, setFarmsWithStructure] = useState([]);
  const [teamUsers, setTeamUsers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [workforceMembers, setWorkforceMembers] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [subscriptionLimits, setSubscriptionLimits] = useState(null);
  const [subscriptionUsage, setSubscriptionUsage] = useState(null);
  const [subscriptionRemaining, setSubscriptionRemaining] = useState(null);
  const [perAnimalPricing, setPerAnimalPricing] = useState(null);
  const [currency, setCurrency] = useState(getAppCurrency());
  // Farm saved from the sidebar switcher (validated against the farm list below).
  const [storedFarmId, setStoredFarmId] = useState(null);

  // Individual loading states
  const [loadingFarms, setLoadingFarms] = useState(false);
  const [loadingTeamUsers, setLoadingTeamUsers] = useState(false);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [loadingSubscription, setLoadingSubscription] = useState(false);

  // Error states
  const [errors, setErrors] = useState({
    farms: null,
    teamUsers: null,
    contacts: null,
    subscription: null,
  });

  const initRef = useRef(false);
  const isInitializingRef = useRef(false);
  const userRef = useRef(user);
  userRef.current = user;

  // Fetch farms with location structure
  const fetchFarms = useCallback(async (showLoading = true) => {
    if (showLoading) {
      setLoadingFarms(true);
    }
    try {
      const response = await api.get('/farms/with-location-structure');
      const data = response.data.data || response.data;
      const farmsArray = Array.isArray(data) ? data : [];
      setFarmsWithStructure(farmsArray);
      setErrors(prev => ({ ...prev, farms: null }));
      return farmsArray;
    } catch (error) {
      // Members without the Farms module can't load farm structures; use the farms they are assigned to.
      const assignedFarms = userRef.current?.farms;
      if (error.response?.status === 403 && Array.isArray(assignedFarms)) {
        setFarmsWithStructure(assignedFarms);
        setErrors(prev => ({ ...prev, farms: null }));
        return assignedFarms;
      }
      console.error('Failed to fetch farms:', error);
      setErrors(prev => ({ ...prev, farms: error.message || 'Failed to fetch farms' }));
      throw error;
    } finally {
      if (showLoading) {
        setLoadingFarms(false);
      }
    }
  }, []);

  // Fetch team users
  const fetchTeamUsers = useCallback(async (showLoading = true) => {
    if (showLoading) {
      setLoadingTeamUsers(true);
    }
    try {
      // Request minimal fields (id, name, email) for bootstrap data
      const response = await api.get('/team/users', { params: { paginate: false, minimal: true } });
      const data = response.data.data || response.data;
      const usersArray = Array.isArray(data) ? data : [];
      setTeamUsers(usersArray);
      setErrors(prev => ({ ...prev, teamUsers: null }));
      return usersArray;
    } catch (error) {
      console.error('Failed to fetch team users:', error);
      setErrors(prev => ({ ...prev, teamUsers: error.message || 'Failed to fetch team users' }));
      throw error;
    } finally {
      if (showLoading) {
        setLoadingTeamUsers(false);
      }
    }
  }, []);

  const fetchWorkforceMembers = useCallback(async (showLoading = false) => {
    try {
      const response = await api.get('/contacts', {
        params: { paginate: false, workforce_member: 1 },
      });
      const data = response.data.data || response.data;
      const list = Array.isArray(data) ? data : [];
      setWorkforceMembers(list);
      return list;
    } catch (error) {
      console.error('Failed to fetch workforce members:', error);
      setWorkforceMembers([]);
      throw error;
    }
  }, []);

  // Fetch contacts
  const fetchContacts = useCallback(async (showLoading = true) => {
    if (showLoading) {
      setLoadingContacts(true);
    }
    try {
      // Request minimal fields (id, first_name, last_name, company) for bootstrap data
      const response = await api.get('/contacts', { params: { paginate: false, minimal: true } });
      const data = response.data.data || response.data;
      const contactsArray = Array.isArray(data) ? data : [];
      setContacts(contactsArray);
      setErrors(prev => ({ ...prev, contacts: null }));
      return contactsArray;
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
      setErrors(prev => ({ ...prev, contacts: error.message || 'Failed to fetch contacts' }));
      throw error;
    } finally {
      if (showLoading) {
        setLoadingContacts(false);
      }
    }
  }, []);

  // Fetch subscription
  const fetchSubscription = useCallback(async (showLoading = true) => {
    if (showLoading) {
      setLoadingSubscription(true);
    }
    try {
      const response = await api.get('/subscriptions/current');
      setSubscription(response.data.subscription);
      setSubscriptionLimits(response.data.limits);
      setSubscriptionUsage(response.data.current_usage || null);
      setSubscriptionRemaining(response.data.remaining || null);
      setPerAnimalPricing(response.data.per_animal_pricing || null);
      setErrors(prev => ({ ...prev, subscription: null }));
      return {
        subscription: response.data.subscription,
        limits: response.data.limits,
        usage: response.data.current_usage,
        remaining: response.data.remaining,
        per_animal_pricing: response.data.per_animal_pricing,
      };
    } catch (error) {
      // 404 or any error - treat as no subscription (don't throw, just return null)
      // This allows the app to continue functioning even if subscription fetch fails
      if (error.response?.status === 404 || error.response?.status === 200) {
        // 200 with null subscription is also valid
        const subscription = error.response?.data?.subscription || null;
        setSubscription(subscription);
        setSubscriptionLimits(error.response?.data?.limits || null);
        setSubscriptionUsage(error.response?.data?.current_usage || null);
        setSubscriptionRemaining(error.response?.data?.remaining || null);
        setPerAnimalPricing(error.response?.data?.per_animal_pricing || null);
        setErrors(prev => ({ ...prev, subscription: null }));
        return {
          subscription: subscription,
          limits: error.response?.data?.limits || null,
          usage: error.response?.data?.current_usage || null,
          remaining: error.response?.data?.remaining || null,
          per_animal_pricing: error.response?.data?.per_animal_pricing || null,
        };
      }
      // For other errors, log but don't block the app
      console.error('Failed to fetch subscription:', error);
      setSubscription(null);
      setSubscriptionLimits(null);
      setPerAnimalPricing(null);
      setErrors(prev => ({ ...prev, subscription: error.message || 'Failed to fetch subscription' }));
      return { subscription: null, limits: null, per_animal_pricing: null };
    } finally {
      if (showLoading) {
        setLoadingSubscription(false);
      }
    }
  }, []);

  // Bootstrap: Load all data when user is authenticated
  const bootstrap = useCallback(async () => {
    if (!isAuthenticated || !user || isInitializingRef.current) {
      return;
    }

    if (typeof window !== 'undefined' && isPublicAppPath(window.location.pathname)) {
      return;
    }

    // Prevent duplicate initialization
    if (initRef.current) {
      return;
    }

    isInitializingRef.current = true;
    setLoading(true);

    try {
      const isSuperAdmin = user?.is_super_admin === true;

      // Super admins don't have subscriptions, skip loading subscription data
      if (isSuperAdmin) {
        initRef.current = true;
        setInitialized(true);
        isInitializingRef.current = false;
        setLoading(false);
        return; // Exit early, don't load subscription, farms, users, contacts
      }

      // Check subscription status FIRST before loading other data
      // This prevents unnecessary API calls if account is locked
      let subscriptionData;
      try {
        subscriptionData = await fetchSubscription(false);
      } catch (error) {
        // If subscription fetch fails, treat as no subscription but don't block the app
        // This allows new users to access dashboard and see wizard
        subscriptionData = { subscription: null, limits: null };
      }

      // Check if account is locked or subscription is not active
      const isLocked = subscriptionData.subscription?.is_locked === true;
      const isNotActive = subscriptionData.subscription && !subscriptionData.subscription.is_fully_active;

      // Skip loading farms, users, contacts for:
      // 1. Locked accounts (but still allow dashboard access)
      // 2. Inactive subscriptions (but still allow dashboard access)
      // Note: We don't block dashboard access - let it show wizard or upgrade page as needed
      if (isLocked || isNotActive) {
        initRef.current = true;
        setInitialized(true);
        isInitializingRef.current = false;
        setLoading(false);
        return; // Exit early, don't load farms, users, contacts
      }

      // If no subscription but user is account admin, they might be new
      // Allow them to access dashboard to see wizard
      // But still try to load farms/users/contacts if subscription exists
      if (!subscriptionData.subscription && user?.is_account_admin) {
        // New account admin - allow dashboard access
        // Still try to load other data (they might have farms even without subscription)
        try {
          await Promise.all([
            fetchFarms(false),
            fetchTeamUsers(false),
            fetchContacts(false),
            fetchWorkforceMembers(false),
          ]);
        } catch (error) {
          // Ignore errors - allow app to continue
          console.error('Failed to load some data:', error);
        }

        initRef.current = true;
        setInitialized(true);
        isInitializingRef.current = false;
        setLoading(false);
        return;
      }

      // Load other data in parallel only if account is active and not super admin
      await Promise.all([
        fetchFarms(false),
        fetchTeamUsers(false),
        fetchContacts(false),
        fetchWorkforceMembers(false),
      ]);

      initRef.current = true;
      setInitialized(true);
    } catch (error) {
      console.error('Bootstrap error:', error);
      // Even if there's an error, mark as initialized to prevent infinite loops
      initRef.current = true;
      setInitialized(true);
    } finally {
      setLoading(false);
      isInitializingRef.current = false;
    }
  }, [isAuthenticated, user, fetchFarms, fetchTeamUsers, fetchContacts, fetchWorkforceMembers, fetchSubscription]);

  // Refresh all data
  const refreshAll = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchFarms(false),
        fetchTeamUsers(false),
        fetchContacts(false),
        fetchWorkforceMembers(false),
        fetchSubscription(false),
      ]);
    } catch (error) {
      console.error('Refresh all error:', error);
    } finally {
      setLoading(false);
    }
  }, [fetchFarms, fetchTeamUsers, fetchContacts, fetchSubscription]);

  // Refresh individual resources
  const refreshFarms = useCallback(() => fetchFarms(true), [fetchFarms]);
  const refreshTeamUsers = useCallback(() => fetchTeamUsers(true), [fetchTeamUsers]);
  const refreshContacts = useCallback(async () => {
    await fetchContacts(true);
    return fetchWorkforceMembers(false);
  }, [fetchContacts, fetchWorkforceMembers]);
  const refreshSubscription = useCallback(() => fetchSubscription(true), [fetchSubscription]);

  // Bootstrap when user becomes authenticated
  // Wait for auth to finish loading, then bootstrap if authenticated
  useEffect(() => {
    // Don't bootstrap if auth is still loading
    if (authLoading) {
      return;
    }

    // Bootstrap if user is authenticated and we haven't initialized yet
    if (isAuthenticated && user && !initRef.current && !isPublicAppPath(window.location.pathname)) {
      bootstrap();
    }
  }, [isAuthenticated, user, authLoading, bootstrap]);

  // Clear data on logout
  useEffect(() => {
    if (!isAuthenticated) {
      animalHierarchyCache.clear();
      setStoredFarmId(null);
      setFarmsWithStructure([]);
      setTeamUsers([]);
      setContacts([]);
      setSubscription(null);
      setSubscriptionLimits(null);
      setSubscriptionUsage(null);
      setSubscriptionRemaining(null);
      setPerAnimalPricing(null);
      setInitialized(false);
      initRef.current = false;
      setCurrency(getAppCurrency());
      setErrors({
        farms: null,
        teamUsers: null,
        contacts: null,
        subscription: null,
      });
    }
  }, [isAuthenticated]);

  const updateCurrency = useCallback((code) => {
    const next = persistAppCurrency(code);
    setCurrency(next);
  }, []);

  useEffect(() => {
    const syncCurrency = (event) => {
      const next = event?.detail || getAppCurrency();
      setCurrency(next);
    };
    window.addEventListener(APP_CURRENCY_CHANGE_EVENT, syncCurrency);
    return () => window.removeEventListener(APP_CURRENCY_CHANGE_EVENT, syncCurrency);
  }, []);

  // Helper: Get simple farms list (for backward compatibility)
  const farms = React.useMemo(() => {
    return farmsWithStructure.map(farm => ({
      id: farm.id || farm.farm_id,
      name: farm.name || farm.farm_name,
      location: farm.location_city || farm.location,
      city: farm.location_city || farm.city || farm.location,
      country: farm.location_country || farm.country || (farm.location ? 'Pakistan' : ''),
      address: farm.location_address || farm.address,
      latitude: farm.latitude,
      longitude: farm.longitude,
      area: farm.area,
      farm_type: farm.farm_type,
      zones: farm.zones,
      sheds: farm.sheds,
      lots: farm.lots,
    }));
  }, [farmsWithStructure]);

  const selectedFarmStorageKey = user?.id ? `selectedFarmId:${user.id}` : null;

  useEffect(() => {
    if (!selectedFarmStorageKey) return;
    try {
      setStoredFarmId(localStorage.getItem(selectedFarmStorageKey));
    } catch (e) {
      setStoredFarmId(null);
    }
  }, [selectedFarmStorageKey]);

  // Derived (not an effect) so pages see the farm on their first render with the farm list.
  // null means "All farms". A saved farm the user can no longer access falls back to all;
  // a single farm is always selected.
  const selectedFarmId = React.useMemo(() => {
    if (farms.length === 1) return farms[0].id;
    const match = farms.find((farm) => String(farm.id) === String(storedFarmId));
    return match ? match.id : null;
  }, [farms, storedFarmId]);

  const setSelectedFarmId = useCallback((farmId) => {
    const next = farmId == null || farmId === '' ? null : farmId;
    setStoredFarmId(next == null ? null : String(next));
    if (!selectedFarmStorageKey) return;
    try {
      if (next == null) localStorage.removeItem(selectedFarmStorageKey);
      else localStorage.setItem(selectedFarmStorageKey, String(next));
    } catch (e) {
      // Storage unavailable; the selection still applies for this session.
    }
  }, [selectedFarmStorageKey]);

  const selectedFarm = React.useMemo(
    () => farms.find((farm) => farm.id === selectedFarmId) || null,
    [farms, selectedFarmId]
  );

  const value = {
    // Data
    farms,
    selectedFarmId,
    selectedFarm,
    setSelectedFarmId,
    farmsWithStructure,
    teamUsers,
    contacts,
    workforceMembers,
    subscription,
    subscriptionLimits,
    subscriptionUsage,
    subscriptionRemaining,
    perAnimalPricing,
    currency,

    // Loading states
    loading,
    loadingFarms,
    loadingTeamUsers,
    loadingContacts,
    loadingSubscription,
    initialized,

    // Errors
    errors,

    // Actions
    refreshAll,
    refreshFarms,
    refreshTeamUsers,
    refreshContacts,
    refreshWorkforceMembers: fetchWorkforceMembers,
    refreshSubscription,
    bootstrap,

    // Direct fetch functions (for advanced use cases)
    fetchFarms,
    fetchTeamUsers,
    fetchContacts,
    fetchWorkforceMembers,
    fetchSubscription,

    // App preferences
    setCurrency: updateCurrency,
  };

  return <AppDataContext.Provider value={value}>{children}</AppDataContext.Provider>;
};

export const useAppData = () => {
  const context = useContext(AppDataContext);
  if (!context) {
    throw new Error('useAppData must be used within an AppDataProvider');
  }
  return context;
};

